import { useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { useCennikMaterialy } from "@/api/hooks/useCennik";
import {
  useDeleteDielec,
  useQuickCreateDielec,
  useUpdateDielec,
} from "@/api/hooks/useDielce";
import { toast } from "@/components/ui/use-toast";
import { extractErrorMessage } from "@/api/client";
import { suggestNextKod } from "@/lib/dielecKod";
import { formatEur, normalizeNum, toNum } from "@/lib/format";
import type { Dielec, Material, Zostava } from "@/api/schemas";

/**
 * Kalkulačka skla podľa cenníka Sklenárstva Mier — ekvivalent
 * „ponuka" sheetu dodávateľa:
 *
 *   typ skla × hrúbka × (šírka × výška × ks) + kalenie + vŕtanie
 *   + služby (leštenie hrán, fólia, výrezy…)
 *
 * Po potvrdení sa do zostavy vložia ROZPÍSANÉ riadky kusovníka
 * (sklo + kalenie + vŕtanie + služby) — každý s materiálom z cenníka,
 * takže ceny sa počítajú štandardným pricingom a pri update cenníka
 * sa dajú prepočítať.
 */

interface SluzbaVyber {
  checked: boolean;
  /** bm služby: bm na 1 sklo; ks služby: počet na 1 sklo. */
  mnozstvo: string;
}

/** Výrobná cena za MJ — rovnaký vzorec ako backend pricing. */
function vyrobnaCena(m: Material): number {
  const nakup = parseFloat(String(m.nakup_cena)) || 0;
  const rezia = parseFloat(String(m.rezia_pct)) || 0;
  const praca = parseFloat(String(m.praca_eur_per_mj)) || 0;
  return nakup * (1 + rezia / 100) + praca;
}

export interface SkloCreateDialogProps {
  zakazkaId: string;
  zostavy: Zostava[];
  defaultZostavaId?: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Edit mód: existujúci sklo dielec — dialóg sa prefillne a pri
   * uložení prepíše rodiča + nanovo vytvorí súčasti. */
  editDielec?: Dielec | null;
  /** Súčasti editovaného skla (deti: kalenie, vŕtanie, služby). */
  editSucasti?: Dielec[];
}

export function SkloCreateDialog({
  zakazkaId,
  zostavy,
  defaultZostavaId = null,
  open,
  onOpenChange,
  editDielec = null,
  editSucasti = [],
}: SkloCreateDialogProps): JSX.Element {
  const createMutation = useQuickCreateDielec(zakazkaId);
  const updateMutation = useUpdateDielec(zakazkaId);
  const deleteMutation = useDeleteDielec(zakazkaId);
  const isEdit = !!editDielec;

  // Celý cenník Sklenárstva Mier naraz (sklá + kalenie + služby).
  // Sklo je inde v Cenníku skryté — `sekcia: "sklo"` si oň výslovne
  // povie (KOLO 164); dodávateľ ostáva, nech tam nie je iné sklo.
  const cennikQuery = useCennikMaterialy({
    sekcia: "sklo",
    dodavatel: "sklenarstvo_mier",
    per_page: 300,
  });
  const polozky = useMemo(
    () => cennikQuery.data?.data ?? [],
    [cennikQuery.data],
  );

  const skla = useMemo(
    () => polozky.filter((m) => m.kategoria === "SKLO"),
    [polozky],
  );
  const kalenia = useMemo(
    () => polozky.filter((m) => m.kategoria === "SKLO_KALENIE"),
    [polozky],
  );
  const sluzbyAll = useMemo(
    () => polozky.filter((m) => m.kategoria === "SKLO_SLUZBA"),
    [polozky],
  );
  const vrtania = useMemo(
    () =>
      sluzbyAll
        .filter((m) => m.sklo_mier_id?.startsWith("SKLO_SLUZBA_VRTANIE"))
        .sort((a, b) => vyrobnaCena(a) - vyrobnaCena(b)),
    [sluzbyAll],
  );

  // Typy skla — z supplier_data.typ (poradie podľa cenníka)
  const typy = useMemo(() => {
    const seen: string[] = [];
    for (const m of skla) {
      const t = (m.supplier_data as { typ?: string } | null)?.typ;
      if (t && !seen.includes(t)) seen.push(t);
    }
    return seen;
  }, [skla]);

  // ── stav formulára ──
  const [zostavaId, setZostavaId] = useState<string>("");
  const [typ, setTyp] = useState<string>("");
  const [skloId, setSkloId] = useState<string>("");
  const [x, setX] = useState("");
  const [y, setY] = useState("");
  const [ks, setKs] = useState("1");
  const [kalenie, setKalenie] = useState(false);
  const [otvory, setOtvory] = useState("0");
  const [vrtanieId, setVrtanieId] = useState<string>("");
  const [sluzby, setSluzby] = useState<Record<string, SluzbaVyber>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setZostavaId(defaultZostavaId ?? zostavy[0]?.id ?? "");
      setTyp("");
      setSkloId("");
      setX("");
      setY("");
      setKs("1");
      setKalenie(false);
      setOtvory("0");
      setVrtanieId("");
      setSluzby({});
      prefilledRef.current = false;
    }
  }, [open, defaultZostavaId, zostavy]);

  // Edit mód: prefill z existujúceho dielca + súčastí, keď je cenník
  // načítaný (typ/hrúbka sa hľadajú v položkách cenníka).
  const prefilledRef = useRef(false);
  useEffect(() => {
    if (!open || !editDielec || prefilledRef.current || skla.length === 0) {
      return;
    }
    prefilledRef.current = true;
    setZostavaId(editDielec.zostava_id);
    const parentKs = editDielec.pocet_ks ?? 1;
    setKs(String(parentKs));
    setX(normalizeNum(editDielec.x_mm));
    setY(normalizeNum(editDielec.y_mm));
    const skloItem = skla.find((m) => m.id === editDielec.material_id_direct);
    if (skloItem) {
      setTyp(
        (skloItem.supplier_data as { typ?: string } | null)?.typ ?? "",
      );
      setSkloId(skloItem.id);
    }
    const noveSluzby: Record<string, SluzbaVyber> = {};
    for (const s of editSucasti) {
      const matId = s.material_id_direct;
      if (!matId) continue;
      if (kalenia.some((m) => m.id === matId)) {
        setKalenie(true);
        continue;
      }
      const vrt = vrtania.find((m) => m.id === matId);
      if (vrt) {
        setVrtanieId(vrt.id);
        setOtvory(String(Math.round((s.pocet_ks ?? 0) / parentKs)));
        continue;
      }
      const sluzba = sluzbyAll.find((m) => m.id === matId);
      if (sluzba) {
        const j =
          (sluzba.supplier_data as { sluzba_jednotka?: string } | null)
            ?.sluzba_jednotka ?? "ks";
        let mnozstvo = "1";
        if (j === "bm") {
          mnozstvo = String(
            (parseFloat(String(s.x_mm ?? "0")) || 0) / 1000,
          );
        } else if (j === "ks") {
          mnozstvo = String(Math.round((s.pocet_ks ?? 0) / parentKs));
        }
        noveSluzby[sluzba.id] = { checked: true, mnozstvo };
      }
    }
    setSluzby(noveSluzby);
  }, [open, editDielec, editSucasti, skla, kalenia, vrtania, sluzbyAll]);

  // Hrúbky dostupné pre vybraný typ
  const hrubkyPreTyp = useMemo(
    () =>
      skla
        .filter(
          (m) => (m.supplier_data as { typ?: string } | null)?.typ === typ,
        )
        .sort((a, b) => {
          const la = String(
            (a.supplier_data as { hrubka_label?: string } | null)
              ?.hrubka_label ?? "",
          );
          const lb = String(
            (b.supplier_data as { hrubka_label?: string } | null)
              ?.hrubka_label ?? "",
          );
          return parseFloat(la) - parseFloat(lb) || la.localeCompare(lb);
        }),
    [skla, typ],
  );

  const sklo = useMemo(
    () => skla.find((m) => m.id === skloId) ?? null,
    [skla, skloId],
  );
  const skloSd = sklo?.supplier_data as
    | { kalitelne?: boolean; hrubka_label?: string }
    | null
    | undefined;
  const hrubkaNum = sklo?.z_mm != null ? parseFloat(String(sklo.z_mm)) : null;

  const kalenieItem = useMemo(() => {
    if (hrubkaNum == null) return null;
    return (
      kalenia.find(
        (m) => m.z_mm != null && parseFloat(String(m.z_mm)) === hrubkaNum,
      ) ?? null
    );
  }, [kalenia, hrubkaNum]);
  const kalenieMozne = Boolean(skloSd?.kalitelne && kalenieItem);

  // bm služby s hrúbkovými variantmi: vyber variantu podľa hrúbky skla
  const sluzbyPonuka = useMemo(() => {
    const bez = sluzbyAll.filter(
      (m) => !m.sklo_mier_id?.startsWith("SKLO_SLUZBA_VRTANIE"),
    );
    return bez.filter((m) => {
      const id = m.sklo_mier_id ?? "";
      if (id.endsWith("_3_6")) return hrubkaNum == null || hrubkaNum <= 6;
      if (id.endsWith("_8_10")) return hrubkaNum != null && hrubkaNum >= 8;
      return true;
    });
  }, [sluzbyAll, hrubkaNum]);

  const xN = toNum(x) || 0;
  const yN = toNum(y) || 0;
  const ksN = parseInt(ks, 10) || 1;
  const plochaM2 = (xN * yN) / 1e6;
  const obvodBm = (2 * (xN + yN)) / 1000;
  const otvoryN = parseInt(otvory, 10) || 0;
  const vrtanie = vrtania.find((m) => m.id === vrtanieId) ?? null;

  function jednotkaOf(m: Material): string {
    return (
      (m.supplier_data as { sluzba_jednotka?: string } | null)
        ?.sluzba_jednotka ?? "ks"
    );
  }

  // ── živý cenový súhrn ──
  const riadky = useMemo(() => {
    const out: { label: string; cena: number }[] = [];
    if (sklo && plochaM2 > 0) {
      out.push({
        label: `${sklo.nazov} — ${plochaM2.toFixed(3)} m² × ${ksN} ks`,
        cena: vyrobnaCena(sklo) * plochaM2 * ksN,
      });
    }
    if (kalenie && kalenieItem && plochaM2 > 0) {
      out.push({
        label: `${kalenieItem.nazov} — ${plochaM2.toFixed(3)} m² × ${ksN} ks`,
        cena: vyrobnaCena(kalenieItem) * plochaM2 * ksN,
      });
    }
    if (otvoryN > 0 && vrtanie) {
      out.push({
        label: `${vrtanie.nazov} — ${otvoryN} otv. × ${ksN} ks`,
        cena: vyrobnaCena(vrtanie) * otvoryN * ksN,
      });
    }
    for (const m of sluzbyPonuka) {
      const v = sluzby[m.id];
      if (!v?.checked) continue;
      const j = jednotkaOf(m);
      if (j === "m2") {
        out.push({
          label: `${m.nazov} — ${plochaM2.toFixed(3)} m² × ${ksN} ks`,
          cena: vyrobnaCena(m) * plochaM2 * ksN,
        });
      } else if (j === "bm") {
        const bm = toNum(v.mnozstvo) || 0;
        out.push({
          label: `${m.nazov} — ${bm.toFixed(2)} bm × ${ksN} ks`,
          cena: vyrobnaCena(m) * bm * ksN,
        });
      } else {
        const n = parseInt(v.mnozstvo, 10) || 0;
        out.push({
          label: `${m.nazov} — ${n} ks × ${ksN} skiel`,
          cena: vyrobnaCena(m) * n * ksN,
        });
      }
    }
    return out;
  }, [sklo, kalenie, kalenieItem, plochaM2, ksN, otvoryN, vrtanie, sluzby, sluzbyPonuka]);

  const spolu = riadky.reduce((sum, r) => sum + r.cena, 0);

  function toggleSluzba(m: Material): void {
    setSluzby((prev) => {
      const cur = prev[m.id];
      const j = jednotkaOf(m);
      const defaultMnozstvo =
        j === "bm" ? (obvodBm > 0 ? obvodBm.toFixed(2) : "") : "1";
      return {
        ...prev,
        [m.id]: {
          checked: !cur?.checked,
          mnozstvo: cur?.mnozstvo || defaultMnozstvo,
        },
      };
    });
  }

  /** Vytvorí súčasti (deti) skla — kalenie, vŕtanie, služby. */
  async function vytvorSucasti(
    parentId: string | undefined,
    baseKod: string,
    poradieStart: number,
  ): Promise<number> {
    const spolocne = {
      pocet_ks: ksN,
      nadmiera_x_mm: "0",
      nadmiera_y_mm: "0",
      nadmiera_aktivna: false,
      smer_vlakien: "0" as const,
      hrana_x1_auto: false,
      hrana_x2_auto: false,
      hrana_y1_auto: false,
      hrana_y2_auto: false,
    };
    let poradie = poradieStart;
    let sucasti = 0;
    // kalenie
    if (kalenie && kalenieItem) {
      sucasti += 1;
      await createMutation.mutateAsync({
        zostavaId,
        data: {
          ...spolocne,
          kod: `${baseKod}_kal`,
          nazov_strojovy: kalenieItem.nazov,
          material_id_direct: kalenieItem.id,
          parent_dielec_id: parentId,
          x_mm: String(xN),
          y_mm: String(yN),
          poradie: poradie++,
        },
      });
    }
    // vŕtanie — kusová položka, počet = otvory × ks skiel
    if (otvoryN > 0 && vrtanie) {
      sucasti += 1;
      await createMutation.mutateAsync({
        zostavaId,
        data: {
          ...spolocne,
          kod: `${baseKod}_vrt`,
          nazov_strojovy: vrtanie.nazov,
          material_id_direct: vrtanie.id,
          parent_dielec_id: parentId,
          pocet_ks: otvoryN * ksN,
          poradie: poradie++,
        },
      });
    }
    // služby
    let i = 0;
    for (const m of sluzbyPonuka) {
      const v = sluzby[m.id];
      if (!v?.checked) continue;
      i += 1;
      const j = jednotkaOf(m);
      if (j === "bm") {
        const bm = toNum(v.mnozstvo) || 0;
        if (bm <= 0) continue;
        sucasti += 1;
        await createMutation.mutateAsync({
          zostavaId,
          data: {
            ...spolocne,
            kod: `${baseKod}_s${i}`,
            nazov_strojovy: m.nazov,
            material_id_direct: m.id,
            parent_dielec_id: parentId,
            x_mm: String(Math.round(bm * 1000)),
            poradie: poradie++,
          },
        });
      } else if (j === "m2") {
        sucasti += 1;
        await createMutation.mutateAsync({
          zostavaId,
          data: {
            ...spolocne,
            kod: `${baseKod}_s${i}`,
            nazov_strojovy: m.nazov,
            material_id_direct: m.id,
            parent_dielec_id: parentId,
            x_mm: String(xN),
            y_mm: String(yN),
            poradie: poradie++,
          },
        });
      } else {
        const n = parseInt(v.mnozstvo, 10) || 0;
        if (n <= 0) continue;
        sucasti += 1;
        await createMutation.mutateAsync({
          zostavaId,
          data: {
            ...spolocne,
            kod: `${baseKod}_s${i}`,
            nazov_strojovy: m.nazov,
            material_id_direct: m.id,
            parent_dielec_id: parentId,
            pocet_ks: n * ksN,
            poradie: poradie++,
          },
        });
      }
    }
    return sucasti;
  }

  async function handleSubmit(): Promise<void> {
    const zostava = zostavy.find((z) => z.id === zostavaId);
    if (!zostava || !sklo || xN <= 0 || yN <= 0) {
      toast({
        variant: "destructive",
        title: "Chýbajú údaje",
        description: "Vyber zostavu, typ skla, hrúbku a zadaj rozmery.",
      });
      return;
    }
    setSaving(true);
    try {
      let sucasti = 0;
      if (isEdit && editDielec) {
        // Edit: prepíš rodiča, zmaž staré súčasti a vytvor ich nanovo
        // podľa aktuálneho výberu (množstvá závisia od rozmerov/ks).
        await updateMutation.mutateAsync({
          id: editDielec.id,
          data: {
            nazov_strojovy: sklo.nazov,
            material_id_direct: sklo.id,
            x_mm: String(xN),
            y_mm: String(yN),
            z_mm: hrubkaNum != null ? String(hrubkaNum) : null,
            pocet_ks: ksN,
          },
        });
        for (const s of editSucasti) {
          await deleteMutation.mutateAsync(s.id);
        }
        sucasti = await vytvorSucasti(
          editDielec.id,
          editDielec.kod,
          (editDielec.poradie ?? 0) + 1,
        );
      } else {
        const dielce = (zostava.dielce ?? []) as Dielec[];
        const baseKod = suggestNextKod(zostava.kod, dielce);
        let poradie = dielce.length;
        // sklo — rodič; súčasti (kalenie, vŕtanie, služby) sa vkladajú
        // ako jeho deti, takže v tabuľke je viditeľný len tento riadok.
        const created = await createMutation.mutateAsync({
          zostavaId,
          data: {
            pocet_ks: ksN,
            nadmiera_x_mm: "0",
            nadmiera_y_mm: "0",
            nadmiera_aktivna: false,
            smer_vlakien: "0" as const,
            hrana_x1_auto: false,
            hrana_x2_auto: false,
            hrana_y1_auto: false,
            hrana_y2_auto: false,
            kod: baseKod,
            nazov_strojovy: sklo.nazov,
            material_id_direct: sklo.id,
            x_mm: String(xN),
            y_mm: String(yN),
            z_mm: hrubkaNum != null ? String(hrubkaNum) : null,
            poradie: poradie++,
          },
        });
        sucasti = await vytvorSucasti(created.data?.id, baseKod, poradie);
      }
      toast({
        title: isEdit ? "Sklo upravené" : "Sklo pridané",
        description:
          sucasti > 0
            ? `1 riadok (+${sucasti} súčastí v cene), spolu ${formatEur(spolu)}`
            : `1 riadok, spolu ${formatEur(spolu)}`,
      });
      onOpenChange(false);
    } catch (err) {
      toast({
        variant: "destructive",
        title: isEdit ? "Chyba pri úprave skla" : "Chyba pri pridávaní skla",
        description: extractErrorMessage(err),
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? `Upraviť sklo — ${editDielec?.kod ?? ""}` : "Pridať sklo"}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Sklo — typ, hrúbka, rozmer a služby ako kalenie či vŕtanie.
          </DialogDescription>
        </DialogHeader>

        {cennikQuery.isLoading ? (
          <p className="text-sm text-muted-foreground">Načítavam cenník…</p>
        ) : skla.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Cenník skla nie je naimportovaný — nahraj ho v Nastaveniach
            → Ceny a cenníky → Dodávatelia — zdrojové cenníky.
          </p>
        ) : (
          <div className="space-y-4">
            {zostavy.length > 1 && !isEdit ? (
              <div className="space-y-1.5">
                <Label htmlFor="sk-zostava">Zostava</Label>
                <Select value={zostavaId} onValueChange={setZostavaId}>
                  <SelectTrigger id="sk-zostava" aria-label="Zostava">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {zostavy.map((z) => (
                      <SelectItem key={z.id} value={z.id}>
                        {z.kod} — {z.nazov}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null}

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="sk-typ">Typ skla *</Label>
                <Select
                  value={typ}
                  onValueChange={(v) => {
                    setTyp(v);
                    setSkloId("");
                    setKalenie(false);
                  }}
                >
                  <SelectTrigger id="sk-typ" aria-label="Typ skla">
                    <SelectValue placeholder="Vyber typ…" />
                  </SelectTrigger>
                  <SelectContent>
                    {typy.map((t) => (
                      <SelectItem key={t} value={t}>
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="sk-hrubka">Hrúbka *</Label>
                <Select
                  value={skloId}
                  onValueChange={(v) => {
                    setSkloId(v);
                    setKalenie(false);
                  }}
                >
                  <SelectTrigger
                    id="sk-hrubka"
                    aria-label="Hrúbka skla"
                    disabled={!typ}
                  >
                    <SelectValue placeholder={typ ? "Vyber hrúbku…" : "—"} />
                  </SelectTrigger>
                  <SelectContent>
                    {hrubkyPreTyp.map((m) => {
                      const label = (
                        m.supplier_data as { hrubka_label?: string } | null
                      )?.hrubka_label;
                      return (
                        <SelectItem key={m.id} value={m.id}>
                          {label}
                          {m.z_mm != null ? " mm" : ""} —{" "}
                          {formatEur(vyrobnaCena(m))}/m²
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="sk-x">Šírka X (mm) *</Label>
                <Input
                  id="sk-x"
                  inputMode="decimal"
                  value={x}
                  onChange={(e) => setX(e.target.value)}
                  placeholder="700"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="sk-y">Výška Y (mm) *</Label>
                <Input
                  id="sk-y"
                  inputMode="decimal"
                  value={y}
                  onChange={(e) => setY(e.target.value)}
                  placeholder="500"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="sk-ks">Počet ks</Label>
                <Input
                  id="sk-ks"
                  inputMode="numeric"
                  value={ks}
                  onChange={(e) => setKs(e.target.value)}
                />
              </div>
            </div>

            <Separator />

            {/* Kalenie + vŕtanie */}
            <div className="grid grid-cols-2 gap-3">
              <label
                className={
                  "flex items-center gap-2 text-sm " +
                  (kalenieMozne ? "cursor-pointer" : "opacity-50")
                }
                title={
                  kalenieMozne
                    ? undefined
                    : sklo
                      ? "Tento typ/hrúbka skla sa nekalí"
                      : "Najprv vyber sklo"
                }
              >
                <input
                  type="checkbox"
                  checked={kalenie}
                  disabled={!kalenieMozne}
                  onChange={(e) => setKalenie(e.target.checked)}
                  className="h-4 w-4 accent-primary"
                />
                <span>
                  Kalenie
                  {kalenieItem ? (
                    <span className="text-xs text-muted-foreground">
                      {" "}
                      ({formatEur(vyrobnaCena(kalenieItem))}/m²)
                    </span>
                  ) : null}
                </span>
              </label>
              <div className="flex items-end gap-2">
                <div className="space-y-1.5 w-20">
                  <Label htmlFor="sk-otvory">Otvory / ks</Label>
                  <Input
                    id="sk-otvory"
                    inputMode="numeric"
                    value={otvory}
                    onChange={(e) => setOtvory(e.target.value)}
                  />
                </div>
                <div className="flex-1 space-y-1.5">
                  <Label>Priemer otvoru</Label>
                  <Select
                    value={vrtanieId}
                    onValueChange={setVrtanieId}
                    disabled={otvoryN <= 0}
                  >
                    <SelectTrigger aria-label="Priemer otvoru">
                      <SelectValue placeholder="—" />
                    </SelectTrigger>
                    <SelectContent>
                      {vrtania.map((m) => (
                        <SelectItem key={m.id} value={m.id}>
                          {m.nazov.replace("Vŕtanie skla ", "")} —{" "}
                          {formatEur(vyrobnaCena(m))}/otvor
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Služby */}
            <div className="space-y-1">
              <Label>Služby</Label>
              <div className="max-h-48 overflow-y-auto rounded-md border divide-y">
                {sluzbyPonuka.map((m) => {
                  const v = sluzby[m.id];
                  const j = jednotkaOf(m);
                  return (
                    <div
                      key={m.id}
                      className="flex items-center gap-2 px-2.5 py-1.5 text-sm"
                    >
                      <input
                        type="checkbox"
                        checked={v?.checked ?? false}
                        onChange={() => toggleSluzba(m)}
                        aria-label={m.nazov}
                        className="h-4 w-4 accent-primary"
                      />
                      <span className="flex-1 truncate" title={m.nazov}>
                        {m.nazov.replace(/^Sklo — /, "")}
                      </span>
                      <span className="text-xs text-muted-foreground font-mono whitespace-nowrap">
                        {formatEur(vyrobnaCena(m))}/{j === "m2" ? "m²" : j}
                      </span>
                      {v?.checked && j !== "m2" ? (
                        <Input
                          value={v.mnozstvo}
                          onChange={(e) =>
                            setSluzby((prev) => ({
                              ...prev,
                              [m.id]: { checked: true, mnozstvo: e.target.value },
                            }))
                          }
                          aria-label={`Množstvo ${m.nazov}`}
                          title={j === "bm" ? "bm na 1 sklo (default obvod)" : "počet na 1 sklo"}
                          inputMode="decimal"
                          className="h-6 w-16 px-1 text-xs font-mono"
                        />
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Cenový súhrn */}
            <div className="rounded-md border bg-background-alt/40 p-2.5 space-y-1">
              {riadky.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  Vyber sklo a zadaj rozmery — cena sa spočíta priebežne.
                </p>
              ) : (
                <>
                  {riadky.map((r, idx) => (
                    <div
                      key={idx}
                      className="flex justify-between gap-3 text-xs"
                    >
                      <span className="truncate text-muted-foreground">
                        {r.label}
                      </span>
                      <span className="font-mono whitespace-nowrap">
                        {formatEur(r.cena)}
                      </span>
                    </div>
                  ))}
                  <div className="flex justify-between border-t pt-1 text-sm font-semibold">
                    <span>Spolu</span>
                    <span className="font-mono text-primary" data-testid="sklo-spolu">
                      {formatEur(spolu)}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            Zrušiť
          </Button>
          <Button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={saving || !sklo || xN <= 0 || yN <= 0}
          >
            {saving
              ? "Ukladá sa…"
              : isEdit
                ? "Uložiť"
                : "Pridať do zostavy"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
